import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Search,
  Trash2,
  User,
  BookOpen,
  Loader,
  RefreshCw,
  X,
  Eye,
  RefreshCw as SyncIcon,
  Download,
  FileText,
  Barcode,
  CheckSquare,
  Square,
  AlertCircle,
  CheckCircle2,
  Filter
} from 'lucide-react';
import api from '../api';

const Members = () => {
  const navigate = useNavigate();
  
  // Primary States
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Search and Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);
  
  // Modals
  const [showMemberDetails, setShowMemberDetails] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState('pdf');
  const [downloadType, setDownloadType] = useState('barcodes'); // Default to barcodes for quick print
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Toast & Dialog States (Replaces default alerts)
  const [toast, setToast] = useState(null); // { message, type: 'success' | 'error' }
  const [confirmModal, setConfirmModal] = useState(null); // { title, message, actionText, onConfirm }

  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    pages: 0
  });
  
  // Summary Stats
  const [memberStats, setMemberStats] = useState({
    total: 0,
    students: 0,
    faculty: 0,
    staff: 0
  });
  
  const [syncing, setSyncing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const tenantId = localStorage.getItem('tenantId');

  // ==================== TOAST HELPER ====================
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ==================== FETCH MEMBERS ====================
  const fetchMembers = useCallback(async (page = 1) => {
    setLoading(true);

    try {
      const params = {
        tenantId,
        page,
        limit: pagination.limit
      };

      if (searchTerm) params.search = searchTerm;
      if (selectedRole !== 'all') params.role = selectedRole;
      if (selectedStatus !== 'all') params.isActive = selectedStatus === 'active';

      const response = await api.get('/api/library/members', { params });

      if (response.data.success) {
        setMembers(response.data.data);
        setPagination(prev => ({
          ...prev,
          page,
          total: response.data.pagination?.total || response.data.data.length,
          pages: response.data.pagination?.pages || 1
        }));
        setSelectedMembers([]);
        setSelectAll(false);
      }
    } catch (err) {
      console.error('Error fetching members:', err);
      showToast(err.response?.data?.message || 'Could not load library members list.', 'error');
    } finally {
      setLoading(false);
    }
  }, [tenantId, pagination.limit, searchTerm, selectedRole, selectedStatus]);

  // ==================== FETCH SUMMARY STATS ====================
  const fetchMemberStats = useCallback(async () => {
    try {
      const response = await api.get('/api/library/members/stats', {
        params: { tenantId }
      });
      if (response.data.success) {
        setMemberStats(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [tenantId]);

  // ==================== SYNC USERS ====================
  const handleSyncMembers = () => {
    if (!tenantId) {
      showToast('Missing Tenant ID. Please log in again.', 'error');
      return;
    }

    setConfirmModal({
      title: 'Import College Accounts?',
      message: 'This will copy all active students and staff into your library list so you can issue books to them.',
      actionText: 'Import Now',
      onConfirm: async () => {
        setSyncing(true);
        try {
          const response = await api.post('/api/library/members/sync', {}, {
            params: { tenantId },
            timeout: 120000
          });

          if (response.data.success) {
            showToast(response.data.message || 'Users imported successfully!');
            await fetchMembers(1);
            await fetchMemberStats();
          }
        } catch (err) {
          showToast(err.response?.data?.message || 'Failed to import users.', 'error');
        } finally {
          setSyncing(false);
          setConfirmModal(null);
        }
      }
    });
  };

  // ==================== DOWNLOAD FILES ====================
  const handleDownload = async () => {
    if (!tenantId) {
      showToast('Missing Tenant ID. Please log in again.', 'error');
      return;
    }

    const memberIds = selectedMembers.length > 0 ? selectedMembers : null;
    
    setDownloading(true);
    try {
      let endpoint = '';
      let params = { tenantId };
      
      if (downloadType === 'barcodes') {
        endpoint = '/api/library/members/download-barcodes';
      } else {
        endpoint = '/api/library/members/download';
        params.format = downloadFormat;
      }

      const response = await api.post(endpoint, 
        { memberIds },
        { 
          params,
          responseType: 'blob',
          timeout: 120000
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      let extension = downloadType === 'barcodes' ? 'pdf' : downloadFormat;
      if (extension === 'excel') extension = 'xlsx';
      
      const filename = downloadType === 'barcodes' 
        ? `member_barcodes_${Date.now()}.pdf`
        : `library_members_${Date.now()}.${extension}`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setShowDownloadModal(false);
      setSelectedMembers([]);
      setSelectAll(false);
      showToast('Download completed successfully!');
      
    } catch (err) {
      console.error('Download failed:', err);
      showToast('Download failed. Please try again.', 'error');
    } finally {
      setDownloading(false);
    }
  };

  // ==================== TOGGLE SELECTION ====================
  const toggleMemberSelection = (memberId) => {
    setSelectedMembers(prev => 
      prev.includes(memberId) ? prev.filter(id => id !== memberId) : [...prev, memberId]
    );
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(members.map(m => m.id));
    }
    setSelectAll(!selectAll);
  };

  // ==================== TOGGLE STATUS ====================
  const handleToggleStatus = (memberId, currentStatus) => {
    const actionName = currentStatus ? 'block' : 'unblock';
    
    setConfirmModal({
      title: `${actionName === 'block' ? 'Block' : 'Unblock'} Library Member?`,
      message: currentStatus 
        ? 'This member will not be allowed to borrow books until you unblock them.' 
        : 'This member will be able to borrow books again.',
      actionText: actionName === 'block' ? 'Block Member' : 'Unblock Member',
      onConfirm: async () => {
        try {
          const response = await api.patch(`/api/library/members/${memberId}/toggle-status`, null, {
            params: { tenantId }
          });

          if (response.data.success) {
            showToast(`Member is now ${response.data.data.isActive ? 'Active' : 'Blocked'}`);
            await fetchMembers(pagination.page);
            await fetchMemberStats();
          }
        } catch (err) {
          showToast(err.response?.data?.message || 'Could not change member status.', 'error');
        } finally {
          setConfirmModal(null);
        }
      }
    });
  };

  // ==================== DELETE MEMBER ====================
  const handleDeleteMember = (memberId) => {
    setConfirmModal({
      title: 'Remove Member?',
      message: 'Are you sure you want to remove this person from the library? Make sure they have returned all books first.',
      actionText: 'Delete Member',
      onConfirm: async () => {
        try {
          const response = await api.delete(`/api/library/members/${memberId}`, {
            params: { tenantId }
          });

          if (response.data.success) {
            showToast('Member removed successfully!');
            await fetchMembers(pagination.page);
            await fetchMemberStats();
          }
        } catch (err) {
          showToast(err.response?.data?.message || 'Could not delete member. Check if they still have books.', 'error');
        } finally {
          setConfirmModal(null);
        }
      }
    });
  };

  // Role Pill Configs
  const getRoleStyle = (role) => {
    const roleLower = role?.toLowerCase() || '';
    if (roleLower === 'student') return 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border-blue-200 dark:border-blue-900';
    if (['faculty', 'teacher'].includes(roleLower)) return 'bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 border-purple-200 dark:border-purple-900';
    return 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 border-zinc-200 dark:border-zinc-700';
  };

  // Effects
  useEffect(() => {
    if (tenantId) {
      fetchMembers();
      fetchMemberStats();
    }
  }, [tenantId, fetchMembers, fetchMemberStats]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (tenantId) fetchMembers(1);
    }, 350);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, selectedRole, selectedStatus, tenantId, fetchMembers]);

  return (
    <div className="space-y-4 max-w-7xl mx-auto p-3 sm:p-5 font-sans">
      
      {/* Floating Toast Notification */}
      {toast && (
        <div className="fixed bottom-5 right-5 z-50 flex items-center space-x-2 px-4 py-3 rounded-xl shadow-lg border text-sm font-semibold transition-all animate-bounce bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white border-zinc-200 dark:border-zinc-800">
          {toast.type === 'error' ? (
            <AlertCircle className="h-5 w-5 text-rose-500" />
          ) : (
            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
          )}
          <span>{toast.message}</span>
        </div>
      )}

      {/* Header Banner */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pb-3 border-b border-zinc-200 dark:border-zinc-800">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-zinc-900 dark:text-white tracking-tight">
            Library Members
          </h1>
          <p className="text-xs sm:text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">
            Manage students, teachers, and staff borrowing library books.
          </p>
        </div>
        
        {/* Top Header Buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowDownloadModal(true)}
            disabled={members.length === 0}
            className="flex-1 sm:flex-initial px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs sm:text-sm font-bold shadow-sm transition-all flex items-center justify-center space-x-1.5 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            <span>Print / Export</span>
          </button>
          
          <button
            onClick={handleSyncMembers}
            disabled={syncing}
            className="flex-1 sm:flex-initial px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs sm:text-sm font-bold shadow-sm transition-all flex items-center justify-center space-x-1.5 disabled:opacity-50"
          >
            {syncing ? <Loader className="h-4 w-4 animate-spin" /> : <SyncIcon className="h-4 w-4" />}
            <span>Import Users</span>
          </button>
        </div>
      </div>

      {/* Quick Summary Cards (Scrollable on small phones) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl p-4 border border-zinc-200 dark:border-zinc-800 shadow-sm">
          <p className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider">Total Members</p>
          <div className="flex justify-between items-end mt-1">
            <p className="text-2xl font-black text-zinc-900 dark:text-white">{memberStats.total || 0}</p>
            <Users className="h-5 w-5 text-indigo-500 mb-1" />
          </div>
        </div>

        <div className="bg-white dark:bg-zinc-900 rounded-2xl p-4 border border-zinc-200 dark:border-zinc-800 shadow-sm">
          <p className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider">Students</p>
          <div className="flex justify-between items-end mt-1">
            <p className="text-2xl font-black text-blue-600 dark:text-blue-400">{memberStats.students || 0}</p>
            <User className="h-5 w-5 text-blue-500 mb-1" />
          </div>
        </div>

        <div className="bg-white dark:bg-zinc-900 rounded-2xl p-4 border border-zinc-200 dark:border-zinc-800 shadow-sm">
          <p className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider">Teachers / Faculty</p>
          <div className="flex justify-between items-end mt-1">
            <p className="text-2xl font-black text-purple-600 dark:text-purple-400">{memberStats.faculty || 0}</p>
            <User className="h-5 w-5 text-purple-500 mb-1" />
          </div>
        </div>

        <div className="bg-white dark:bg-zinc-900 rounded-2xl p-4 border border-zinc-200 dark:border-zinc-800 shadow-sm">
          <p className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider">Office Staff</p>
          <div className="flex justify-between items-end mt-1">
            <p className="text-2xl font-black text-zinc-700 dark:text-zinc-300">{memberStats.staff || 0}</p>
            <User className="h-5 w-5 text-zinc-400 mb-1" />
          </div>
        </div>
      </div>

      {/* Search and Filters Bar */}
      <div className="bg-white dark:bg-zinc-900 rounded-2xl p-3 border border-zinc-200 dark:border-zinc-800 shadow-sm space-y-3 md:space-y-0 md:flex md:gap-2">
        {/* Search Field */}
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <input
            type="text"
            placeholder="Search by name, USN, email, or barcode..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 text-xs sm:text-sm rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 text-zinc-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Filter Toggle Mobile */}
        <button
          onClick={() => setShowFiltersMobile(!showFiltersMobile)}
          className="w-full md:hidden py-2 px-3 bg-zinc-100 dark:bg-zinc-800 rounded-xl text-xs font-bold text-zinc-700 dark:text-zinc-300 flex items-center justify-center space-x-2"
        >
          <Filter className="h-3.5 w-3.5" />
          <span>{showFiltersMobile ? 'Hide Filters' : 'Show Filters'}</span>
        </button>

        {/* Filter Dropdowns */}
        <div className={`flex flex-col sm:flex-row gap-2 ${showFiltersMobile ? 'block' : 'hidden md:flex'}`}>
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="px-3 py-2.5 text-xs sm:text-sm rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 text-zinc-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Roles</option>
            <option value="student">Students</option>
            <option value="faculty">Teachers / Faculty</option>
            <option value="staff">Staff</option>
          </select>

          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-3 py-2.5 text-xs sm:text-sm rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 text-zinc-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active Members</option>
            <option value="inactive">Blocked Members</option>
          </select>

          <button
            onClick={() => fetchMembers(1)}
            className="px-3 py-2.5 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 text-zinc-700 dark:text-zinc-300 rounded-xl text-xs font-bold transition-all flex items-center justify-center space-x-1"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Selected Items Sticky Bar */}
      {selectedMembers.length > 0 && (
        <div className="bg-indigo-600 text-white rounded-2xl p-3 px-4 flex items-center justify-between shadow-md">
          <div className="flex items-center space-x-2 text-xs sm:text-sm font-bold">
            <CheckSquare className="h-4 w-4" />
            <span>{selectedMembers.length} member(s) selected</span>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowDownloadModal(true)}
              className="px-3 py-1 bg-white text-indigo-700 rounded-lg text-xs font-extrabold hover:bg-indigo-50"
            >
              Print Barcodes
            </button>
            <button
              onClick={() => setSelectedMembers([])}
              className="text-xs text-indigo-200 hover:text-white underline"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Select All Checkbox for Screen Readers/Touch */}
      {members.length > 0 && (
        <div className="flex items-center justify-between px-2 pt-1 text-xs text-zinc-500">
          <button
            onClick={toggleSelectAll}
            className="flex items-center space-x-2 font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            {selectAll ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
            <span>{selectAll ? 'Deselect All' : 'Select All On This Page'}</span>
          </button>
          <span>Showing {members.length} member(s)</span>
        </div>
      )}

      {/* Loading Skeleton / Member Grid */}
      {loading && members.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 space-y-3 bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800">
          <Loader className="h-8 w-8 text-indigo-600 animate-spin" />
          <p className="text-xs sm:text-sm font-bold text-zinc-500">Loading library members list...</p>
        </div>
      ) : members.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
          {members.map((member) => {
            const isSelected = selectedMembers.includes(member.id);
            const isBlocked = member.isActive === false;

            return (
              <div
                key={member.id}
                className={`bg-white dark:bg-zinc-900 rounded-2xl p-4 border transition-all flex flex-col justify-between shadow-sm relative ${
                  isSelected 
                    ? 'border-indigo-500 ring-2 ring-indigo-500/20' 
                    : 'border-zinc-200 dark:border-zinc-800'
                }`}
              >
                <div>
                  {/* Card Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => toggleMemberSelection(member.id)}
                        className="p-1 -ml-1 text-zinc-400 hover:text-indigo-600"
                      >
                        {isSelected ? (
                          <CheckSquare className="h-5 w-5 text-indigo-600" />
                        ) : (
                          <Square className="h-5 w-5" />
                        )}
                      </button>

                      <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 font-black text-sm flex items-center justify-center">
                        {member.name?.charAt(0)?.toUpperCase() || 'M'}
                      </div>

                      <div className="max-w-[130px] sm:max-w-[160px] truncate">
                        <h3 className="text-sm font-bold text-zinc-900 dark:text-white truncate">
                          {member.name}
                        </h3>
                        <p className="text-[11px] text-zinc-400 truncate">{member.email || 'No Email'}</p>
                      </div>
                    </div>

                    {/* Quick View Button */}
                    <button
                      onClick={() => setShowMemberDetails(member)}
                      className="p-1.5 text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 bg-zinc-50 dark:bg-zinc-800 rounded-lg"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Badges & ID Info */}
                  <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
                    <span className={`px-2 py-0.5 rounded-md font-bold border ${getRoleStyle(member.role)}`}>
                      {member.role ? member.role.toUpperCase() : 'USER'}
                    </span>

                    {isBlocked ? (
                      <span className="px-2 py-0.5 rounded-md font-bold bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400 border border-rose-200 dark:border-rose-900">
                        BLOCKED
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-md font-bold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900">
                        ACTIVE
                      </span>
                    )}

                    {member.barcodeNumber && (
                      <span className="px-2 py-0.5 rounded-md font-mono font-bold bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                        #{member.barcodeNumber}
                      </span>
                    )}
                  </div>

                  {/* Checked Out Books Info */}
                  <div className="mt-3 pt-2.5 border-t border-zinc-100 dark:border-zinc-800 flex items-center justify-between text-xs">
                    <span className="text-zinc-400 flex items-center">
                      <BookOpen className="h-3.5 w-3.5 mr-1" />
                      Books Issued:
                    </span>
                    <span className={`font-black ${member.activeBooksCount > 0 ? 'text-amber-600' : 'text-zinc-700 dark:text-zinc-300'}`}>
                      {member.activeBooksCount || 0}
                    </span>
                  </div>
                </div>

                {/* Bottom Card Action Buttons */}
                <div className="mt-4 pt-2 flex gap-2">
                  <button
                    onClick={() => navigate(`/librarian/transactions/issue?memberId=${member.id}`)}
                    disabled={isBlocked}
                    className="flex-1 py-2 px-2 bg-indigo-50 dark:bg-indigo-950/50 hover:bg-indigo-100 text-indigo-700 dark:text-indigo-300 rounded-xl text-xs font-bold transition-all disabled:opacity-40"
                  >
                    Issue Book
                  </button>

                  <button
                    onClick={() => handleToggleStatus(member.id, member.isActive)}
                    className={`py-2 px-3 rounded-xl text-xs font-bold transition-all ${
                      isBlocked 
                        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 hover:bg-emerald-100'
                        : 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 hover:bg-zinc-200'
                    }`}
                  >
                    {isBlocked ? 'Unblock' : 'Block'}
                  </button>

                  <button
                    onClick={() => handleDeleteMember(member.id)}
                    className="p-2 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-xl"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white dark:bg-zinc-900 rounded-2xl p-8 text-center border border-zinc-200 dark:border-zinc-800">
          <Users className="h-10 w-10 text-zinc-300 mx-auto mb-2" />
          <h3 className="text-base font-bold text-zinc-900 dark:text-white">No Members Found</h3>
          <p className="text-xs text-zinc-500 mt-1 max-w-xs mx-auto">
            Try searching for a different name, or click Import Users to copy students from your college system.
          </p>
          <button
            onClick={handleSyncMembers}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white text-xs font-bold rounded-xl"
          >
            Import College Users
          </button>
        </div>
      )}

      {/* Pagination Bar */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between pt-3 text-xs text-zinc-500">
          <span>Page {pagination.page} of {pagination.pages}</span>
          <div className="flex space-x-2">
            <button
              onClick={() => fetchMembers(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="px-3 py-1.5 rounded-lg border border-zinc-200 dark:border-zinc-700 disabled:opacity-40 font-bold"
            >
              Back
            </button>
            <button
              onClick={() => fetchMembers(pagination.page + 1)}
              disabled={pagination.page >= pagination.pages}
              className="px-3 py-1.5 rounded-lg border border-zinc-200 dark:border-zinc-700 disabled:opacity-40 font-bold"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* ==================== DOWNLOAD / PRINT MODAL ==================== */}
      {showDownloadModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-zinc-950/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-zinc-900 rounded-t-3xl sm:rounded-3xl shadow-xl max-w-md w-full border border-zinc-200 dark:border-zinc-800 overflow-hidden p-5 space-y-4">
            
            <div className="flex justify-between items-center pb-2 border-b border-zinc-100 dark:border-zinc-800">
              <h3 className="text-base font-extrabold text-zinc-900 dark:text-white flex items-center space-x-2">
                <Download className="h-5 w-5 text-emerald-500" />
                <span>Print Barcodes / Export Data</span>
              </h3>
              <button onClick={() => setShowDownloadModal(false)} className="p-1 text-zinc-400">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Print Type Switcher */}
            <div>
              <label className="text-xs font-bold text-zinc-500 block mb-2">SELECT ACTION</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setDownloadType('barcodes')}
                  className={`p-3 rounded-2xl border text-left flex flex-col justify-between transition-all ${
                    downloadType === 'barcodes' 
                      ? 'border-indigo-600 bg-indigo-50/50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300' 
                      : 'border-zinc-200 dark:border-zinc-800 text-zinc-600'
                  }`}
                >
                  <Barcode className="h-5 w-5 mb-1 text-indigo-600" />
                  <span className="text-xs font-extrabold">Print Barcode Labels</span>
                  <span className="text-[10px] text-zinc-400 mt-0.5">3-Column Sticky Label PDF</span>
                </button>

                <button
                  onClick={() => setDownloadType('data')}
                  className={`p-3 rounded-2xl border text-left flex flex-col justify-between transition-all ${
                    downloadType === 'data' 
                      ? 'border-indigo-600 bg-indigo-50/50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300' 
                      : 'border-zinc-200 dark:border-zinc-800 text-zinc-600'
                  }`}
                >
                  <FileText className="h-5 w-5 mb-1 text-emerald-600" />
                  <span className="text-xs font-extrabold">Export Member List</span>
                  <span className="text-[10px] text-zinc-400 mt-0.5">PDF or Excel Document</span>
                </button>
              </div>
            </div>

            {/* Sub-Format options for data download */}
            {downloadType === 'data' && (
              <div>
                <label className="text-xs font-bold text-zinc-500 block mb-2">FILE FORMAT</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setDownloadFormat('pdf')}
                    className={`py-2 px-3 rounded-xl border text-xs font-bold ${
                      downloadFormat === 'pdf' ? 'border-rose-500 bg-rose-50 text-rose-700' : 'border-zinc-200'
                    }`}
                  >
                    PDF Document
                  </button>
                  <button
                    onClick={() => setDownloadFormat('excel')}
                    className={`py-2 px-3 rounded-xl border text-xs font-bold ${
                      downloadFormat === 'excel' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-zinc-200'
                    }`}
                  >
                    Excel Sheet
                  </button>
                </div>
              </div>
            )}

            {/* Info notice */}
            <div className="p-3 bg-zinc-50 dark:bg-zinc-800 rounded-xl text-xs text-zinc-500">
              {selectedMembers.length > 0 
                ? `Will download file for ${selectedMembers.length} selected member(s).`
                : 'Will download file for ALL active members in the system.'}
            </div>

            {/* Buttons */}
            <div className="flex space-x-2 pt-2">
              <button
                onClick={() => setShowDownloadModal(false)}
                className="flex-1 py-2.5 bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 rounded-xl text-xs font-bold"
              >
                Cancel
              </button>
              <button
                onClick={handleDownload}
                disabled={downloading}
                className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                {downloading ? <Loader className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                <span>{downloading ? 'Preparing...' : 'Start Download'}</span>
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ==================== VIEW MEMBER DETAILS MODAL ==================== */}
      {showMemberDetails && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-zinc-950/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-zinc-900 rounded-t-3xl sm:rounded-3xl shadow-xl max-w-md w-full border border-zinc-200 dark:border-zinc-800 overflow-hidden p-5 space-y-4">
            
            <div className="flex justify-between items-start pb-3 border-b border-zinc-100 dark:border-zinc-800">
              <div className="flex items-center space-x-3">
                <div className="w-11 h-11 rounded-xl bg-indigo-600 text-white font-black flex items-center justify-center text-lg">
                  {showMemberDetails.name?.charAt(0) || 'M'}
                </div>
                <div>
                  <h3 className="text-base font-extrabold text-zinc-900 dark:text-white">{showMemberDetails.name}</h3>
                  <p className="text-xs text-zinc-400">{showMemberDetails.email || 'No Email'}</p>
                </div>
              </div>
              <button onClick={() => setShowMemberDetails(null)} className="p-1 text-zinc-400">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Profile List */}
            <div className="space-y-2.5 text-xs">
              <div className="flex justify-between py-1.5 border-b border-zinc-100 dark:border-zinc-800">
                <span className="text-zinc-400">Barcode ID</span>
                <span className="font-mono font-bold text-indigo-600 dark:text-indigo-400">{showMemberDetails.barcodeNumber || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-zinc-100 dark:border-zinc-800">
                <span className="text-zinc-400">Role</span>
                <span className="font-bold text-zinc-800 dark:text-zinc-200 uppercase">{showMemberDetails.role || 'User'}</span>
              </div>
              {showMemberDetails.usn && (
                <div className="flex justify-between py-1.5 border-b border-zinc-100 dark:border-zinc-800">
                  <span className="text-zinc-400">USN / Roll No.</span>
                  <span className="font-bold font-mono text-zinc-800 dark:text-zinc-200">{showMemberDetails.usn}</span>
                </div>
              )}
              {showMemberDetails.className && (
                <div className="flex justify-between py-1.5 border-b border-zinc-100 dark:border-zinc-800">
                  <span className="text-zinc-400">Class / Stream</span>
                  <span className="font-bold text-zinc-800 dark:text-zinc-200">{showMemberDetails.className}</span>
                </div>
              )}
              <div className="flex justify-between py-1.5 border-b border-zinc-100 dark:border-zinc-800">
                <span className="text-zinc-400">Phone</span>
                <span className="font-bold text-zinc-800 dark:text-zinc-200">{showMemberDetails.phone || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1.5">
                <span className="text-zinc-400">Books Borrowed Now</span>
                <span className="font-black text-amber-600">{showMemberDetails.activeBooksCount || 0} Books</span>
              </div>
            </div>

            <div className="flex space-x-2 pt-2">
              <button
                onClick={() => setShowMemberDetails(null)}
                className="flex-1 py-2.5 bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 rounded-xl text-xs font-bold"
              >
                Close
              </button>
              <button
                onClick={() => {
                  const mId = showMemberDetails.id;
                  setShowMemberDetails(null);
                  navigate(`/librarian/transactions/issue?memberId=${mId}`);
                }}
                disabled={showMemberDetails.isActive === false}
                className="flex-1 py-2.5 bg-indigo-600 text-white rounded-xl text-xs font-bold disabled:opacity-40"
              >
                Issue Book
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ==================== TAILWIND CONFIRMATION MODAL ==================== */}
      {confirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-950/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-zinc-900 rounded-3xl p-5 max-w-sm w-full border border-zinc-200 dark:border-zinc-800 space-y-3">
            <h3 className="text-base font-extrabold text-zinc-900 dark:text-white">{confirmModal.title}</h3>
            <p className="text-xs text-zinc-500 dark:text-zinc-400">{confirmModal.message}</p>
            <div className="flex space-x-2 pt-2">
              <button
                onClick={() => setConfirmModal(null)}
                className="flex-1 py-2.5 bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 rounded-xl text-xs font-bold"
              >
                Cancel
              </button>
              <button
                onClick={confirmModal.onConfirm}
                className="flex-1 py-2.5 bg-indigo-600 text-white rounded-xl text-xs font-bold"
              >
                {confirmModal.actionText || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Members;